import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit'
import { normalize } from 'normalizr'
import { UpdateUserDto, UserEntity, UsersService } from '../../../openapi'
import { RootState } from '../../../store'
import { userSchema } from '../../../store/schema'
import { apiCall } from '../../core'
import { NormalizedUserEntity, PlainUserEntity } from '../entities'
import { fetchCurrentUserAction, logout } from './authentication-view.slice'

// Use the generated UsersService methods

export const updateCurrentUser = createAsyncThunk(
  'users/update',
  async (body: UpdateUserDto, { rejectWithValue }) => {
    return await apiCall(UsersService.usersControllerUpdateV1, rejectWithValue, body)
  }
)

// Entity adapter
const entityAdapter = createEntityAdapter<PlainUserEntity>({
  sortComparer: (entityA, entityB) => (entityA.name || '').localeCompare(entityB.name || ''),
})

const initialState = entityAdapter.getInitialState()

// Define the slice
const slice = createSlice({
  name: 'userEntities',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(logout, () => initialState)

    builder.addCase(updateCurrentUser.fulfilled, (state, action) => {
      const normalizedData = normalize<UserEntity, { users: Record<string, NormalizedUserEntity> }>(
        action.payload,
        userSchema
      )

      const [nextEntity] = Object.values(normalizedData.entities.users || [])
      entityAdapter.upsertOne(state, nextEntity)
    })

    builder.addCase(fetchCurrentUserAction.fulfilled, (state, action) => {
      const normalizedData = normalize<UserEntity, { users: Record<string, NormalizedUserEntity> }>(
        action.payload.user, // Extract the user from MyUserDto
        userSchema
      )

      const nextEntities = Object.values(normalizedData.entities.users || [])
      entityAdapter.upsertMany(state, nextEntities)
    })
  },
})

export const userEntitiesReducer = slice.reducer

// Export the standard reducers and the reducer function
const {
  selectAll: selectAllUsers,
  selectEntities: selectUserEntities,
  selectById: selectUserById,
} = entityAdapter.getSelectors((state: RootState) => state.userEntities)

export { selectUserById, selectUserEntities }

// Custom selector
export const selectUsers = createSelector([selectAllUsers], users => users)
